import axios from "axios";

// Public vehicle pages (no login). The link token in the URL authorises every call; after the SMS code is
// verified the API also needs the session token (sent as `session`, CORS only allows standard headers).
const base = () => process.env.REACT_APP_TENANT_API_URL + "v1/";
const portal = (token) => base() + "vehicle-portal/" + encodeURIComponent(token);
const guest = (token) => base() + "guest-vehicle/" + encodeURIComponent(token);
const access = (token) => base() + "guest-access/" + encodeURIComponent(token);

export const portalApi = {
  requestLink: (contact) => axios.post(base() + "vehicle-portal/request-link", { contact }),
  info: (token) => axios.get(portal(token) + "/info"),
  sendOtp: (token, phone) => axios.post(portal(token) + "/send-otp", { phone }),
  verifyOtp: (token, data) => axios.post(portal(token) + "/verify-otp", data),
  // One-time code from a link opened in the tenant app; replaces the SMS code
  appLogin: (token, code) => axios.post(portal(token) + "/app-login", { code }),
  get:(token, session) => axios.get(portal(token), { params: { session } }),
  submitVehicle: (token, formData) => axios.post(portal(token) + "/vehicles", formData),
  inviteGuest: (token, data) => axios.post(portal(token) + "/guest-invites", data),
  resendInvite: (token, id, session) => axios.post(portal(token) + "/guest-invites/" + id + "/resend", { session }),
  cancelInvite: (token, id, session) => axios.post(portal(token) + "/guest-invites/" + id + "/cancel", { session }),
};

export const guestApi = {
  // With a verified session the response also has the full guest_phone (edit form prefill)
  get: (token, session) => axios.get(guest(token), { params: session ? { session } : {} }),
  status: (token) => axios.get(guest(token) + "/status"),
  sendOtp: (token) => axios.post(guest(token) + "/send-otp"),
  verifyOtp: (token, data) => axios.post(guest(token) + "/verify-otp", data),
  submitDetails: (token, formData) => axios.post(guest(token) + "/details", formData),
  // Edit link the host sent from the app (pending, awaiting payment or active)
  editDetails: (token, formData) => axios.post(guest(token) + "/edit", formData),
  checkout: (token, data) => axios.post(guest(token) + "/checkout", data),
};

// Guest parking access page (/a/:token): gate and door controls after a one-time SMS code
export const accessApi = {
  get: (token) => axios.get(access(token)),
  sendCode: (token) => axios.post(access(token) + "/send-code"),
  verifyCode: (token, code) => axios.post(access(token) + "/verify-code", { code }),
  session: (token, session) => axios.post(access(token) + "/session", { session }),
  open: (token, target, session) => axios.post(access(token) + "/open/" + target, { session }),
  logout: (token, session) => axios.post(access(token) + "/logout", { session }),
};

export const apiErrorMessage = (error, fallback) => {
  if (error?.response?.status === 429) return error?.response?.data?.message || "Too many attempts. Please wait a minute and try again.";
  return error?.response?.data?.message || fallback;
};

// apt_no may already include "Unit" ("Unit 112") or not ("112"): always shown as "Unit 112"
export const unitLabel = (aptNo) => {
  const value = String(aptNo || "").trim().replace(/^(apt\.?|apartment)\s*/i, "").replace(/^unit\s*/i, "").trim();
  return value ? `Unit ${value}` : "";
};

// US phone input formatting while typing: "3135550123" -> "(313) 555-0123" (a leading country code 1 is dropped)
export const formatUsPhone = (value) => {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.length > 10 && digits[0] === "1") digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits.length ? `(${digits}` : "";
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Array extras are sent as repeated `key[]` entries (e.g. keep_photo_ids: [1, 2] -> keep_photo_ids[]=1&keep_photo_ids[]=2)
export const buildVehicleFormData = (values, photos, extra = {}) => {
  const formData = new FormData();
  formData.append("license", values?.license || "");
  formData.append("make", values?.make || "");
  formData.append("model", values?.model || "");
  formData.append("color", values?.color || "");
  Object.keys(extra).forEach((key) => {
    const value = extra[key];
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      const name = key.endsWith("[]") ? key : key + "[]";
      value.forEach((item) => formData.append(name, item));
    } else {
      formData.append(key, value);
    }
  });
  (photos || []).forEach((file) => formData.append("photos[]", file));
  return formData;
};

// Verified session per link, kept for the tab only; storage can be unavailable (private mode, blocked)
const sessionKey = (scope, token) => `vehicle_${scope}_session_${token}`;

export const readSession = (scope, token) => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(sessionKey(scope, token)) || "null");
    return saved && saved.expiresAt > Date.now() ? saved.value : "";
  } catch (e) {
    return "";
  }
};

export const writeSession = (scope, token, value, expiresInSeconds) => {
  try {
    if (value) {
      sessionStorage.setItem(sessionKey(scope, token), JSON.stringify({ value, expiresAt: Date.now() + (expiresInSeconds - 60) * 1000 }));
    } else {
      sessionStorage.removeItem(sessionKey(scope, token));
    }
  } catch (e) {
    console.error("[VehiclePublic] Unable to use session storage", e);
  }
};

// Guest access session: kept on this phone (localStorage) until the session expires, usually when the pass ends
const accessKey = (token) => `guest_access_session_${token}`;

export const readAccessSession = (token) => {
  try {
    const saved = JSON.parse(localStorage.getItem(accessKey(token)) || "null");
    if (saved && saved.value && saved.expiresAt > Date.now()) return saved.value;
    if (saved) localStorage.removeItem(accessKey(token));
    return "";
  } catch (e) {
    return "";
  }
};

export const writeAccessSession = (token, value, expiresAt) => {
  try {
    if (value) {
      const expiry = expiresAt ? new Date(expiresAt).getTime() : NaN;
      localStorage.setItem(accessKey(token), JSON.stringify({ value, expiresAt: Number.isNaN(expiry) ? Date.now() + 12 * 3600 * 1000 : expiry }));
    } else {
      localStorage.removeItem(accessKey(token));
    }
  } catch (e) {
    console.error("[GuestAccess] Unable to use local storage", e);
  }
};
